import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, ExternalLink, Search } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useI18n } from "@/i18n";
import AdminNav from "@/components/AdminNav";
import useTableState from "@/hooks/useTableState";
import { TableToolbar, Th, matchSearch, sortRows } from "@/components/DataTable";

export default function AdminBilling() {
  const { t } = useI18n();
  const [ov, setOv] = useState(null);
  const [email, setEmail] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const bts = useTableState("b", { sort: "renews_at", dir: "asc" });

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/billing/overview");
      setOv(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const lookup = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      const r = await api.get(`/admin/billing/user/${encodeURIComponent(email.trim())}`);
      setDetail(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!ov) return <p className="pt-16 text-center font-mono2 text-sm text-slate-500">{t("common.loading")}</p>;

  const card = "rounded-xl p-4 hud-card";
  const statLabel = "font-mono2 text-[9px] uppercase tracking-widest text-slate-500";
  const statValue = "font-display text-2xl font-black text-slate-50";

  const q = bts.get("q");
  const renewals = sortRows(
    ov.upcoming_renewals.filter((s) => matchSearch(s, q, ["email"])),
    bts.get("sort"), bts.get("dir"),
    bts.get("sort") === "amount" ? (x) => x.amount ?? 0 : undefined
  );
  const failed = ov.failed_payments.filter((f) => matchSearch(f, q, ["email"]));
  const cancellations = ov.cancellations.filter((c) => matchSearch(c, q, ["email", "type"]));

  return (
    <div data-testid="admin-billing-page">
      <h1 className="mb-4 font-display text-3xl font-black uppercase tracking-tight text-slate-50 sm:text-4xl">{t("admin.billing_title")}</h1>
      <AdminNav />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="billing-stats">
        <div className={card}><p className={statLabel}>{t("admin.active_subs")}</p><p className={statValue} data-testid="billing-active-count">{ov.active_count}</p></div>
        <div className={card}><p className={statLabel}>{t("admin.mrr")}</p><p className={`${statValue} text-[#00FF87]`} data-testid="billing-mrr">{ov.mrr} {ov.currency}</p></div>
        <div className={card}>
          <p className={statLabel}>{t("admin.plan_breakdown")}</p>
          {Object.entries(ov.plan_breakdown).map(([k, v]) => <p key={k} className="font-mono2 text-[11px] text-slate-300">{k}: <b>{v}</b></p>)}
          {Object.keys(ov.plan_breakdown).length === 0 && <p className="font-mono2 text-[11px] text-slate-600">—</p>}
        </div>
        <div className={card}>
          <p className={statLabel}>{t("admin.new_subs_title")}</p><p className={statValue}>{ov.new_last_30d.total}</p>
          <div className="mt-1 max-h-16 overflow-y-auto">{ov.new_last_30d.daily.map((d) => <p key={d.date} className="font-mono2 text-[9px] text-slate-500">{d.date}: {d.count}</p>)}</div>
        </div>
      </div>

      {ov.mismatches.length > 0 && (
        <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="billing-mismatches">
          <h2 className="mb-2 flex items-center gap-2 font-mono2 text-xs font-bold uppercase tracking-widest text-amber-400"><AlertTriangle className="h-3.5 w-3.5" /> {t("admin.mismatch_title")}</h2>
          {ov.mismatches.map((m, i) => <p key={i} className="font-mono2 text-[10px] text-amber-300">{m.email} — {m.issue}</p>)}
        </div>
      )}

      <TableToolbar ts={bts} t={t} placeholder={t("admin.email")} />

      <div className="mb-6 grid gap-4 lg:grid-cols-2">
        <div className={card} data-testid="renewals-table">
          <h2 className="mb-3 font-mono2 text-xs font-bold uppercase tracking-widest text-[#00F0FF]">{t("admin.renewals_title")}</h2>
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-800 font-mono2 text-[9px] uppercase text-slate-500">
                <Th k="email" label={t("admin.email")} ts={bts} />
                <Th k="amount" label={t("admin.col_amount")} ts={bts} />
                <Th k="renews_at" label={t("admin.col_renews")} ts={bts} />
              </tr>
            </thead>
            <tbody>
              {renewals.map((s) => (
                <tr key={s.subscription_id} className="border-b border-slate-800/50 font-mono2 text-[10px] text-slate-300" data-testid="renewal-row">
                  <td className="py-1.5 pr-2">{s.email || "—"}</td>
                  <td className="py-1.5 pr-2">{s.amount} {s.currency}</td>
                  <td className="py-1.5">{s.renews_at ? new Date(s.renews_at).toLocaleDateString() : "—"}{s.cancel_at_period_end ? " (cancels)" : ""}</td>
                </tr>
              ))}
              {renewals.length === 0 && <tr><td colSpan={3} className="py-3 text-center text-slate-600">—</td></tr>}
            </tbody>
          </table>
        </div>

        <div className={card} data-testid="failed-payments-table">
          <h2 className="mb-3 font-mono2 text-xs font-bold uppercase tracking-widest text-red-400">{t("admin.failed_title")}</h2>
          {failed.map((f, i) => (
            <div key={i} className="mb-2 flex flex-wrap justify-between gap-2 border-b border-slate-800/50 pb-2 font-mono2 text-[10px] text-slate-300" data-testid="failed-payment-row">
              <span>{f.email || "—"}</span>
              <span className="text-red-400">{f.amount} {f.currency}</span>
              <span className="text-slate-500">{t("admin.col_reason")}: {f.attempt_count ?? "?"} / {f.next_retry ? new Date(f.next_retry).toLocaleDateString() : "—"}</span>
            </div>
          ))}
          {failed.length === 0 && <p className="py-3 text-center text-xs text-slate-600">—</p>}
        </div>
      </div>

      <div className={`${card} mb-6`} data-testid="cancellations-panel">
        <h2 className="mb-3 font-mono2 text-xs font-bold uppercase tracking-widest text-slate-400">{t("admin.cancellations_title")}</h2>
        {cancellations.map((c, i) => (
          <div key={i} className="mb-1.5 flex flex-wrap gap-3 font-mono2 text-[10px] text-slate-400" data-testid="cancellation-row">
            <span>{c.email || "—"}</span>
            <span className="text-slate-600">{c.type}</span>
            <span className="text-slate-500">{c.access_until ? `access until ${new Date(c.access_until).toLocaleDateString()}` : ""} · {c.at ? new Date(c.at).toLocaleString() : ""}</span>
          </div>
        ))}
        {cancellations.length === 0 && <p className="py-2 text-center text-xs text-slate-600">—</p>}
      </div>

      <div className={card} data-testid="billing-drilldown">
        <h2 className="mb-3 font-mono2 text-xs font-bold uppercase tracking-widest text-[#00F0FF]">{t("admin.drilldown_title")}</h2>
        <div className="mb-4 flex gap-2">
          <input
            data-testid="drilldown-email-input"
            className="w-full max-w-xs rounded-md border border-[#1F293D] bg-[#0B0E14] px-3 py-2 text-xs text-slate-200 focus:border-[#00F0FF] focus:outline-none"
            placeholder={t("admin.drilldown_placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookup()}
          />
          <button data-testid="drilldown-button" onClick={lookup} disabled={busy} className="btn-volt disabled:opacity-50"><Search className="h-3.5 w-3.5" /></button>
        </div>
        {detail && (
          <div data-testid="drilldown-result">
            {!detail.stripe ? (
              <p className="text-xs text-slate-500">{t("admin.no_stripe")}{detail.user ? ` — local plan: ${detail.user.plan || "free"}` : ""}</p>
            ) : (
              <>
                <div className="mb-3 flex flex-wrap items-center gap-3 font-mono2 text-[10px] text-slate-400">
                  <span>{detail.stripe.customer_id}</span>
                  {detail.stripe.payment_method && <span>{t("admin.payment_method")}: {detail.stripe.payment_method.brand} •••• {detail.stripe.payment_method.last4}</span>}
                  <a href={detail.stripe.dashboard_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#00F0FF] hover:underline" data-testid="stripe-dashboard-link">
                    {t("admin.open_stripe")} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <p className="mb-1 font-mono2 text-[9px] uppercase text-slate-500">{t("admin.subscriptions")}</p>
                {detail.stripe.subscriptions.map((s) => (
                  <div key={s.subscription_id} className="mb-1.5 flex flex-wrap gap-3 border-b border-slate-800/50 pb-1.5 font-mono2 text-[10px] text-slate-300" data-testid="drilldown-sub-row">
                    <span>{s.plan}</span><span className={s.status === "active" ? "text-emerald-400" : "text-slate-500"}>{s.status}</span>
                    <span>{s.amount} {s.currency}/{s.interval}</span><span className="text-slate-500">→ {s.renews_at ? new Date(s.renews_at).toLocaleDateString() : "—"}</span>
                  </div>
                ))}
                <p className="mb-1 mt-3 font-mono2 text-[9px] uppercase text-slate-500">{t("admin.invoices")}</p>
                {detail.stripe.invoices.map((inv, i) => (
                  <div key={i} className="mb-1 flex flex-wrap items-center gap-3 font-mono2 text-[10px] text-slate-400" data-testid="drilldown-invoice-row">
                    <span>{inv.number || "—"}</span><span>{inv.amount} {inv.currency}</span><span className="text-slate-500">{inv.status}</span>
                    {inv.hosted_url && <a href={inv.hosted_url} target="_blank" rel="noreferrer" className="text-[#00F0FF] hover:underline">PDF ↗</a>}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
